/**
 * FieldLockOverlay.js
 * 
 * Wraps a form field to show:
 * - Lock indicator when another user is editing
 * - Incoming change flash when another user updates the value
 * - Focus/blur handlers for automatic locking
 * 
 * Drop into: /root/legalflow/frontend/src/components/record/FieldLockOverlay.js
 * 
 * Usage in ScreenRenderer:
 *   <FieldLockOverlay
 *     fieldName="client_name"
 *     lock={fieldLocks['client_name']}
 *     incomingChange={incomingChanges['client_name']}
 *     onFocus={() => lockField('client_name')}
 *     onBlur={() => unlockField('client_name')}
 *     onAcceptChange={() => { applyChange('client_name'); clearIncomingChange('client_name'); }}
 *     isReadOnly={false}
 *   >
 *     <input ... />
 *   </FieldLockOverlay>
 */
import React, { useState, useEffect } from 'react';
import { Lock, RefreshCw } from 'lucide-react';

export default function FieldLockOverlay({
  fieldName,
  lock,          // lock object from another user, or null
  incomingChange, // { value, changedBy, savedAt } or null
  onFocus,
  onBlur,
  onAcceptChange,
  isReadOnly,
  children,
}) {
  const [showChangeFlash, setShowChangeFlash] = useState(false);
  const isLockedByOther = lock && lock.userId; // truthy if another user holds the lock

  // Flash animation when an incoming change arrives
  useEffect(() => {
    if (incomingChange) {
      setShowChangeFlash(true);
      const timer = setTimeout(() => setShowChangeFlash(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [incomingChange?.savedAt]);

  return (
    <div
      className="relative group"
      onFocus={!isReadOnly && !isLockedByOther ? onFocus : undefined}
      onBlur={!isReadOnly ? onBlur : undefined}
    >
      {/* The actual field (with pointer-events disabled if locked) */}
      <div className={isLockedByOther ? 'pointer-events-none' : ''}>
        {children}
      </div>

      {/* Lock overlay — semi-transparent cover when locked by another user */}
      {isLockedByOther && (
        <div className="absolute inset-0 bg-gray-100/60 rounded flex items-center justify-end pr-2 cursor-not-allowed z-10">
          <div className="flex items-center gap-1.5 bg-white border rounded-full pl-2 pr-3 py-1 shadow-sm">
            <div
              className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px] font-bold"
              style={{ backgroundColor: lock.color || '#6B7280' }}
            >
              {(lock.firstName || '?')[0]}
            </div>
            <Lock size={11} className="text-gray-400" />
            <span className="text-xs text-gray-500">
              {lock.firstName} is editing
            </span>
          </div>
        </div>
      )}

      {/* Incoming change indicator — flash bar at top of field */}
      {incomingChange && showChangeFlash && (
        <div className="absolute -top-7 left-0 right-0 z-20 animate-fade-in">
          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-t px-2 py-1">
            <span className="text-xs text-blue-700">
              <span className="font-medium">{incomingChange.changedBy?.firstName}</span>
              {' '}updated this field
            </span>
            <button
              onClick={onAcceptChange}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              <RefreshCw size={11} />
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Colored left border when locked by another */}
      {isLockedByOther && (
        <div
          className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l"
          style={{ backgroundColor: lock.color || '#6B7280' }}
        />
      )}
    </div>
  );
}
